import { useState, useEffect, useRef } from "react";
import { 
  Home, 
  BarChart3, 
  LineChart, 
  Settings, 
  Info, 
  MessageSquare,
  HelpCircle,
  Phone,
  ChevronRight,
  Menu,
  X as XIcon,
  Moon,
  Sun,
  Code,
  Wrench,
  Database,
  ChevronDown,
  AlertTriangle,
  Zap,
  ShieldCheck,
  Bot,
  Sparkles,
  Twitter,
  Send
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { CtaButton } from "@/components/CtaButton";
import NextGenImage from "./ui/NextGenImage";

export function MainNavigation({ onNavigate }: { onNavigate?: () => void } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const lastPriceRef = useRef<number | null>(null);
  const [, navigate] = useLocation();
  
  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);
  
  // Toggle theme handler
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };
  
  // Close mobile menu on link click
  const handleLinkClick = (sectionId?: string) => {
    if (onNavigate) onNavigate();
    setMobileMenuOpen(false);
    if (sectionId) {
      if (window.location.pathname !== "/") {
        navigate(`/?scrollTo=${sectionId}`);
      } else {
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          if (section) {
            const headerHeight = 80;
            const elementPosition = section.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
            window.scrollTo({ top: offsetPosition, behavior: "smooth" });
          }
        }, 100);
      }
    }
  };
  
  const scrollToSection = (sectionId: string) => {
    // First close the mobile menu
    handleLinkClick();
    
    // Small delay to allow the menu animation to complete
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        // Scroll to the section with offset for the fixed header
        const headerHeight = 80; // Approximate header height
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      } else {
        console.warn(`Section with ID "${sectionId}" not found`);
      }
    }, 300);
  };
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let isMounted = true;

    const fetchSolPrice = async () => {
      setLoading(true);
      let price: number | null = null;
      try {
        const res = await fetch("/live");
        const data = await res.json();
        if (typeof data?.prices?.SOL === "number" && !isNaN(data.prices.SOL)) {
          price = data.prices.SOL;
        }
      } catch {}
      if (isMounted) {
        if (typeof price === "number" && !isNaN(price)) {
          setSolPrice(price);
          lastPriceRef.current = price;
        } else if (lastPriceRef.current !== null) {
          setSolPrice(lastPriceRef.current);
        } else {
          setSolPrice(null);
        }
        setLoading(false);
      }
    };

    fetchSolPrice();
    interval = setInterval(fetchSolPrice, 5 * 60 * 1000); // Every 5 minutes
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);
  
  return (
    <>
      {/* Navigation Bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? "bg-[#0c0c15]/95 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2">
                <picture>
                  <source srcSet="/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.avif" type="image/avif" />
                  <source srcSet="/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.webp" type="image/webp" />
                  <img 
                    src="/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.svg" 
                    alt="Solana Volume Bot Mascot Logo" 
                    width={40} 
                    height={40} 
                    className="w-10 h-10 transition-transform hover:scale-110 rounded-full bg-black/10" 
                    loading="lazy"
                  />
                </picture>
                <div className="font-bold text-xl hidden sm:block">
                  <span className="text-white">Solana</span>
                  <span className="text-[#14F195]"> Volume Bot</span>
                </div>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-0.5">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("hero")}
              >
                <Home className="mr-1 h-4 w-4" /> Home
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:text-[#14F195] hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("pricing")}
              >
                <LineChart className="mr-1 h-4 w-4" /> Try Now
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("reviews")}
              >
                <MessageSquare className="mr-1 h-4 w-4" /> Reviews
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("features")}
              >
                <BarChart3 className="mr-1 h-4 w-4" /> Features
              </Button>
              
              {/* Added Table of Contents menus to the main menu */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("tools")}
              >
                <Zap className="mr-1 h-4 w-4" /> Tools
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("api")}
              >
                <LineChart className="mr-1 h-4 w-4" /> API
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("faqs")}
              >
                <HelpCircle className="mr-1 h-4 w-4" /> FAQs
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#1e2035] px-2.5"
                onClick={() => handleLinkClick("contact")}
              >
                <Phone className="mr-1 h-4 w-4" /> Contact
              </Button>
            </nav>
            
            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center gap-2">
              {/* Social Links + Live SOL Price */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-lg bg-black/60 border border-[#23272e]/60 shadow-sm ml-2">
                <a href="https://x.com/solvolumebotio" target="_blank" rel="noopener noreferrer" className="hover:text-[#14F195] transition-colors mr-2" aria-label="Twitter">
                  <XIcon className="w-5 h-5 text-gray-400" />
                </a>
                <a href="https://t.me/TheBotDevSupport" target="_blank" rel="noopener noreferrer" className="hover:text-[#14F195] transition-colors mr-2" aria-label="Telegram">
                  <Send className="w-4 h-4 text-[#229ED9]" />
                </a>
                <img src="/images/coins/svg/SOL.svg" alt="SOL" className="w-5 h-5 mr-1" loading="lazy" />
                <span className="font-bold" style={{ color: '#14F195', fontSize: '1rem', lineHeight: '1', display: 'inline-block', verticalAlign: 'middle' }}>
                  {loading ? '...' : solPrice !== null ? `$${solPrice.toFixed(2)}` : '...'}
                </span>
              </div>
              
              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-gray-300 hover:text-white" 
                onClick={toggleTheme}
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
              
              {/* Mobile Menu Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden text-gray-300 hover:text-white" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <XIcon className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div className={`md:hidden fixed top-16 left-0 right-0 bg-[#0c0c15]/95 backdrop-blur-md z-40 overflow-y-auto border-b border-[#1e2035] ${
        mobileMenuOpen ? "shadow-[0_10px_30px_rgba(0,0,0,0.3)]" : ""
      }`} style={{ touchAction: "pan-y", WebkitOverflowScrolling: "touch", maxHeight: 'calc(100vh - 4rem)' }}>
        <div className="flex flex-col p-4 space-y-3">
          <button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("hero")}
            aria-label="Navigate to Home section"
          >
            <Home className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">Home</span>
          </button>
          
          <Button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("pricing")}
            aria-label="Navigate to Pricing section"
          >
            <LineChart className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">Try Now</span>
          </Button>
          
          <button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("reviews")}
            aria-label="Navigate to Reviews section"
          >
            <MessageSquare className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">Reviews</span>
          </button>
          
          <button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("features")}
            aria-label="Navigate to Features section"
          >
            <BarChart3 className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">Features</span>
          </button>
          
          {/* Mobile Contents Submenu - Added all menus with the same icons */}
          <div className="pl-3 border-l-2 border-[#1e2035] mt-2 mb-2">
            <h3 className="text-xs uppercase text-gray-300 font-medium mb-3 pl-2">Additional Pages</h3>
            <div className="space-y-2">
              <button 
                className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
                onClick={() => handleLinkClick("tools")}
                aria-label="Navigate to Tools section"
              >
                <Zap className="mr-3 h-5 w-5 text-[#9945FF]" />
                <span className="text-base font-medium">Tools</span>
              </button>
              
              <button 
                className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
                onClick={() => handleLinkClick("api")}
                aria-label="Navigate to API section"
              >
                <LineChart className="mr-3 h-5 w-5 text-[#9945FF]" />
                <span className="text-base font-medium">API</span>
              </button>

              <button 
                className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
                onClick={() => handleLinkClick("ai-assistant")}
                aria-label="Navigate to AI Advisor section"
              >
                <Bot className="mr-3 h-5 w-5 text-[#9945FF]" />
                <span className="text-base font-medium">AI Advisor</span>
              </button>
            </div>
          </div>
          
          <button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("faqs")}
            aria-label="Navigate to FAQs section"
          >
            <HelpCircle className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">FAQs</span>
          </button>
          
          <button 
            className="w-full flex items-center justify-start text-white hover:bg-[#1e2035] p-3 rounded-md active:bg-[#2a2c48] touch-manipulation"
            onClick={() => handleLinkClick("contact")}
            aria-label="Navigate to Contact section"
          >
            <Phone className="mr-3 h-5 w-5 text-[#14F195]" />
            <span className="text-base font-medium">Contact</span>
          </button>
          
          <CtaButton 
            variant="primary"
            size="md"
            className="w-full mt-4 py-4"
            icon={<ChevronRight className="ml-2 h-5 w-5" />}
          >
            Start Free Trial
          </CtaButton>
        </div>
      </div>
    </>
  );
}